// Comprehensive error handling utility
export const ErrorTypes = {
  NETWORK: "NETWORK",
  API: "API",
  PAYMENT: "PAYMENT",
  SUBSCRIPTION: "SUBSCRIPTION",
  AUTHENTICATION: "AUTHENTICATION",
  VALIDATION: "VALIDATION",
  PADDLE: "PADDLE",
};

export const ErrorMessages = {
  [ErrorTypes.NETWORK]:
    "Network connection error. Please check your internet connection.",
  [ErrorTypes.API]:
    "API service temporarily unavailable. Please try again later.",
  [ErrorTypes.PAYMENT]:
    "Payment processing error. Please try again or contact support.",
  [ErrorTypes.SUBSCRIPTION]:
    "Subscription service error. Please try again later.",
  [ErrorTypes.AUTHENTICATION]: "Authentication error. Please log in again.",
  [ErrorTypes.VALIDATION]: "Invalid input data. Please check your information.",
  [ErrorTypes.PADDLE]:
    "Payment system error. Please try again or contact support.",
};

export class AppError extends Error {
  constructor(type, message, originalError = null, retryable = false) {
    super(message);
    this.name = "AppError";
    this.type = type;
    this.originalError = originalError;
    this.retryable = retryable;
    this.timestamp = new Date().toISOString();
  }
}

export const createError = (
  type,
  customMessage = null,
  originalError = null,
  retryable = false
) => {
  const message =
    customMessage || ErrorMessages[type] || "An unexpected error occurred";
  return new AppError(type, message, originalError, retryable);
};

export const handleApiError = (error) => {
  if (!error.response) {
    // Network error
    return createError(ErrorTypes.NETWORK, null, error, true);
  }

  const status = error.response.status;
  const data = error.response.data;

  switch (status) {
    case 400:
      if (
        data.message?.includes("duplicate") ||
        data.message?.includes("already has")
      ) {
        return createError(
          ErrorTypes.SUBSCRIPTION,
          "You already have an active subscription",
          error,
          false
        );
      }
      return createError(
        ErrorTypes.VALIDATION,
        data.message || "Invalid request data",
        error,
        false
      );

    case 401:
      return createError(
        ErrorTypes.AUTHENTICATION,
        "Authentication required",
        error,
        false
      );

    case 403:
      return createError(
        ErrorTypes.AUTHENTICATION,
        "Access denied",
        error,
        false
      );

    case 408:
      return createError(ErrorTypes.NETWORK, "Request timeout", error, true);

    case 429:
      return createError(
        ErrorTypes.API,
        "Too many requests. Please wait and try again.",
        error,
        true
      );

    case 500:
      return createError(
        ErrorTypes.API,
        "Server error. Please try again later.",
        error,
        true
      );

    case 502:
    case 503:
    case 504:
      return createError(
        ErrorTypes.API,
        "Service temporarily unavailable",
        error,
        true
      );

    default:
      return createError(
        ErrorTypes.API,
        data.message || "An unexpected error occurred",
        error,
        true
      );
  }
};

export const handlePaddleError = (error) => {
  if (error.message?.includes("Paddle SDK not loaded")) {
    return createError(
      ErrorTypes.PADDLE,
      "Payment system not loaded. Please refresh the page.",
      error,
      true
    );
  }

  if (error.message?.includes("checkout")) {
    return createError(
      ErrorTypes.PAYMENT,
      "Payment processing failed. Please try again.",
      error,
      true
    );
  }

  return createError(ErrorTypes.PADDLE, null, error, true);
};

export const logError = (error, context = {}) => {
  const errorLog = {
    timestamp: new Date().toISOString(),
    error: {
      message: error.message,
      type: error.type || "UNKNOWN",
      stack: error.stack,
    },
    context,
    userAgent: navigator.userAgent,
    url: window.location.href,
  };

  console.error("Application Error:", errorLog);

  // In production, you might want to send this to a logging service
  if (process.env.NODE_ENV === "production") {
    // Send to logging service
    // sendToLoggingService(errorLog);
  }
};

export const withErrorHandling = (asyncFunction) => {
  return async (...args) => {
    try {
      return await asyncFunction(...args);
    } catch (error) {
      let handledError;

      if (error.name === "AppError") {
        handledError = error;
      } else if (error.response) {
        handledError = handleApiError(error);
      } else {
        handledError = createError(ErrorTypes.NETWORK, null, error, true);
      }

      logError(handledError, { function: asyncFunction.name, args });
      throw handledError;
    }
  };
};
